/**
 * Phase 7: section-type classification for the frontend.
 *
 * Mirrors the backend's `_section_type_or_default` pattern (see
 * gl_calcs_routes._compute_schedule_e, executive_dashboard_routes,
 * reconciliation_routes, dcaa_audit_routes). Reads the explicit
 * `type` field the executor emits on every formatted section
 * (Phase 4 added it); falls back to legacy v1 key-pattern derivation
 * for cached calc-run journals that pre-date the type tag.
 *
 * Used by frontend components to:
 *   - filter sections by type (fringe / overhead / ga / allocation)
 *   - drive dynamic row tables instead of positional slots
 *     (e.g. "Fringe 1", "Fringe 2" → iterate every fringe section)
 *
 * Returns one of: "fringe" | "allocation" | "overhead" | "ga" |
 * "material_handling" | "schedule_e" | "schedule" | null.
 */
export function sectionType(section) {
  if (!section || typeof section !== "object") return null;
  const explicit = section.type;
  if (typeof explicit === "string" && explicit.length > 0) {
    return explicit;
  }
  return typeFromLegacyKey(section.key || "");
}

/**
 * Derive a section type from a legacy v1 key.
 * Mirrors StructuredRulesExecutor._type_from_legacy_key.
 */
export function typeFromLegacyKey(key) {
  if (!key) return null;
  if (
    key === "fringe_100" ||
    key === "fringe_101" ||
    key === "fringe_pool_fringe_100" ||
    key === "fringe_pool_fringe_101"
  ) {
    return "fringe";
  }
  if (key.startsWith("alloc_")) return "allocation";
  if (/^oh_\d+_(company|client)$/.test(key)) return "overhead";
  if (key === "ga" || key === "ga_b") return "ga";
  if (key === "material_handling") return "material_handling";
  if (key === "schedule_e") return "schedule_e";
  return null;
}

/**
 * Filter a sections array to those of the given type(s).
 *
 *   sectionsOfType(sections, "fringe")
 *   sectionsOfType(sections, "fringe", "overhead")
 */
export function sectionsOfType(sections, ...types) {
  if (!Array.isArray(sections) || types.length === 0) return [];
  const wanted = new Set(types);
  return sections.filter((s) => wanted.has(sectionType(s)));
}
